/**
 * CreateMediaPlanTemplate
 *
 * Purpose: Template definition for the "Create Media Plan" form, defining
 *          which model/view to load and the visual layout of all fields
 * Architecture: Declares a FormTemplate consumed by TemplateForm. Renders every
 *               visible view field through ExtraFields in a 2-column Section, so
 *               the field order is the metadata view's display_order (ORCH-405).
 * Dependencies: ../../components/template-form/types (FormTemplate),
 *               ../../components/template-form/layout (Section)
 * Author: Development Team
 * Created: 2026-02-15
 */

import type { FormTemplate, FormActions } from '../../components/template-form/types';
import { Section } from '../../components/template-form/layout';

/**
 * CreateMediaPlanTemplate — template definition for media plan creation
 *
 * Purpose: Configures the form to fetch the 'mediaplan' model's 'edit-modal' view
 *          and lays out its fields in a 2-column grid
 * Logic: Renders all visible view fields, sorted by display_order, in a 2-column
 *        Section (no heading).
 */
/**
 * resolvePlanningCurrency — resolves the selected currency's ISO code and updates form context
 *
 * Purpose: Reads the selected fd_currency MDM value's meta_data.fd_code and calls
 *          setPlanningCurrency so all currency-type fields display the correct symbol
 * Input: form (FormActions) — provides getSelectedOption and setPlanningCurrency
 */
const resolvePlanningCurrency = (form: FormActions): void => {
  const option = form.getSelectedOption('fd_currency');
  const code = option?.meta_data?.fd_code || null;
  form.setPlanningCurrency(code);
};

export const CreateMediaPlanTemplate: FormTemplate = {
  modelId: 'mediaplan',
  viewType: 'edit-modal',

  hooks: {
    onFieldChange: (fieldDefId, _value, form) => {
      if (fieldDefId === 'fd_currency') {
        resolvePlanningCurrency(form);
      }
    },
    onInit: (_viewFields, form) => {
      // Resolve planning currency from initial value (edit mode)
      if (form.getValue('fd_currency')) {
        resolvePlanningCurrency(form);
      }
    },
  },

  // Field order comes from the edit-modal view's display_order, not from this template:
  // Maestro's field chips walk the same view, so placing fields here would let the two
  // drift apart (ORCH-405). Multiline Comments spans both columns on its own.
  render: ({ ExtraFields }) => (
    <Section columns={2}>
      <ExtraFields columns={2} />
    </Section>
  ),
};
